/**
 * The Przegląd tab's main column: four reads, one answer.
 *
 * - `GET /attention-queue` — the same cache the Pulpit and `/zadania` read (usually warm when the
 *   trainer arrives from either), narrowed to THIS client's open tasks, in the queue's own order.
 * - `GET /customer/{id}/recent-sessions` — the table's sessions.
 * - The client's schedule over the last month — the plan days they missed, which the sessions read
 *   cannot carry (`model/sessionTable`).
 * - `GET /customer/{id}/progress-summary` — the table heading's „N sesji i M rekordy w 30 dni".
 *
 * ── One `pending`, because the column is one answer ──────────────────────────────────────────
 * Whether the tasks section is a Talia, a single card or one quiet line decides where the table
 * stands, so a table drawn before the queue has answered is a table that moves when it does. The
 * tab gates the whole column on this flag.
 *
 * A RETRY is a wait as well: an errored read keeps `isPending: false` while it refetches, so each
 * read counts as pending while it is errored AND fetching (`src/shared/AGENTS.md`).
 *
 * ── A failed table is failed, not shorter ────────────────────────────────────────────────────
 * The table is the sessions and the missed plan days woven together, so either read failing leaves
 * no table: the missed days alone would read as a client who trained nothing and skipped the rest.
 *
 * `ui/` may not touch the generated tree, so every fetch lives here and the component renders
 * finished rows.
 */

import { useQueries } from '@tanstack/react-query';
import { useState } from 'react';

import { customerScheduleQueryOptions, scheduleWindow } from '~/entities/calendar-event';
import { attentionQueueQueryOptions, type AttentionReason } from '~/entities/client';
import { useSessionUser } from '~/entities/session';
import {
  attentionQueue,
  mutedReasonsOf,
  taskSectionOf,
  useHomeFormats,
  type AttentionItem,
} from '~/features/attention-queue';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';
import { addDays, toIsoDate } from '~/shared/lib/calendarDate';

import {
  clientProgressSummaryQueryOptions,
  clientRecentSessionsQueryOptions,
} from '../api/clientDetailQueries';
import {
  toProgressSummary,
  toRecentSessions,
  toScheduleOccurrences,
} from '../api/clientDetailSource';
import {
  PROGRESS_WINDOW_DAYS,
  sessionTable,
  type ProgressSummary,
  type SessionTableRow,
} from '../model/sessionTable';

export interface OverviewState {
  /** The visit's clock, frozen on its first render — every card's weeks and ages are counted to it. */
  readonly now: Date;
  /** This client's open tasks — the queue's Triage and Feedback items — most urgent first. */
  readonly tasks: readonly AttentionItem[];
  readonly tasksFailed: boolean;
  /** The kinds the trainer muted (R18) — trainer-wide, so they may hide work for this client too. */
  readonly mutedReasons: readonly AttentionReason[];
  /** When the queue read last answered — what dates a read as coming after a write. */
  readonly tasksUpdatedAt: number;
  /** Read the queue again after it failed. */
  readonly retryTasks: () => void;
  /** Empty whenever `sessionsFailed` — see the header. */
  readonly sessions: readonly SessionTableRow[];
  /** The sessions read or the schedule read failed. */
  readonly sessionsFailed: boolean;
  /** Read again whichever of the table's two reads failed. */
  readonly retrySessions: () => void;
  /** `null` while the read is out, when it failed, or when it answered without its counts. */
  readonly summary: ProgressSummary | null;
  /** Any of the four reads still in flight. See the header. */
  readonly pending: boolean;
}

export function useOverview(clientId: number): OverviewState {
  const contentLang = useContentLanguage();
  const { formatDate } = useHomeFormats();
  // Seeded once: `new Date()` in the body is a fresh value on every render.
  const [now] = useState(() => new Date());
  const todayIso = toIsoDate(now);

  const [queue, recent, schedule, progress] = useQueries({
    queries: [
      attentionQueueQueryOptions(),
      clientRecentSessionsQueryOptions(clientId, contentLang),
      customerScheduleQueryOptions(
        clientId,
        scheduleWindow(toIsoDate(addDays(now, -(PROGRESS_WINDOW_DAYS - 1))), todayIso),
      ),
      clientProgressSummaryQueryOptions(clientId),
    ],
  });

  // Narrowed to this client on the WIRE, so only their items are projected; the muted kinds below
  // are trainer-wide and read off the whole answer.
  const clientQueue =
    queue.data === undefined
      ? undefined
      : { ...queue.data, items: queue.data.items?.filter((wireItem) => wireItem.subject?.customerId === clientId) };
  const selfCustomerId = useSessionUser()?.selfCustomerId ?? null;
  const tasks = attentionQueue(clientQueue, {
    formatDate,
    // The quick card never prints the queue row's sentence, and its own figure for a lapse reads
    // the roster itself (`useTaskContent`) — so the roster is not a read of this column.
    daysSinceLastWorkout: () => undefined,
    selfCustomerId,
  })
    // The same admission as the Pulpit and `/zadania`: Triage, Feedback, and the personal record.
    .entries.filter((entry) => taskSectionOf(entry) !== null)
    .map((entry) => entry.item);

  // Failed with NOTHING to show: a background refetch that fails keeps the answer already drawn.
  const sessionsFailed =
    (recent.isError && recent.data === undefined) || (schedule.isError && schedule.data === undefined);

  return {
    now,
    tasks,
    tasksFailed: queue.isError,
    mutedReasons: mutedReasonsOf(queue.data),
    tasksUpdatedAt: queue.dataUpdatedAt,
    retryTasks: () => {
      void queue.refetch();
    },
    sessions: sessionsFailed
      ? []
      : sessionTable(
          toRecentSessions(recent.data) ?? [],
          toScheduleOccurrences(schedule.data) ?? [],
          todayIso,
        ),
    sessionsFailed,
    retrySessions: () => {
      if (recent.isError) void recent.refetch();
      if (schedule.isError) void schedule.refetch();
    },
    summary: toProgressSummary(progress.data),
    // A retry after a failure with nothing on screen brings the wait back. A read that failed a
    // BACKGROUND refetch still holds its answer, and must not put the drawn column back into the
    // wait on the next refetch — that would drop the open card, a draft and the keyboard.
    pending: [queue, recent, schedule, progress].some(
      (read) => read.isPending || (read.isError && read.isFetching && read.data === undefined),
    ),
  };
}
